/**
 * Camera Preferences Utility
 *
 * This utility provides functions to manage camera preferences in localStorage,
 * allowing the application to remember user's camera selection across sessions.
 */

// Storage keys for camera preferences
const CAMERA_PREFERENCE_KEY = "webcam_preferred_camera";
const CAMERA_PREFERENCES_KEY = "webcam_camera_preferences";

/**
 * Load camera preferences from localStorage
 * @returns {Object|null} Camera preferences object or null if none found
 */
export const loadCameraPreferences = () => {
  try {
    const savedDeviceId = localStorage.getItem(CAMERA_PREFERENCE_KEY);
    const savedPreferences = localStorage.getItem(CAMERA_PREFERENCES_KEY);

    if (savedPreferences) {
      return JSON.parse(savedPreferences);
    }

    // Fallback to legacy single device ID storage
    if (savedDeviceId) {
      return { preferredDeviceId: savedDeviceId };
    }

    return null;
  } catch (error) {
    console.warn("Failed to load camera preferences:", error);
    return null;
  }
};

/**
 * Save camera preferences to localStorage
 * @param {string} deviceId - The camera device ID
 * @param {string} deviceLabel - The camera device label/name
 */
export const saveCameraPreferences = (deviceId, deviceLabel = "") => {
  try {
    const preferences = {
      preferredDeviceId: deviceId,
      deviceLabel: deviceLabel,
      lastUpdated: new Date().toISOString(),
      timestamp: Date.now(),
    };

    localStorage.setItem(CAMERA_PREFERENCE_KEY, deviceId);
    localStorage.setItem(CAMERA_PREFERENCES_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.warn("Failed to save camera preferences:", error);
  }
};

/**
 * Clear camera preferences from localStorage
 */
export const clearCameraPreferences = () => {
  try {
    localStorage.removeItem(CAMERA_PREFERENCE_KEY);
    localStorage.removeItem(CAMERA_PREFERENCES_KEY);
  } catch (error) {
    console.warn("Failed to clear camera preferences:", error);
  }
};

/**
 * Check if a device ID matches the saved preference
 * @param {string} deviceId - The camera device ID to check
 * @returns {boolean} True if the device is the preferred one
 */
export const isPreferredCamera = (deviceId) => {
  const preferences = loadCameraPreferences();
  return preferences?.preferredDeviceId === deviceId;
};

/**
 * Get the preferred device ID from saved preferences
 * @returns {string|null} The preferred device ID or null if none saved
 */
export const getPreferredDeviceId = () => {
  const preferences = loadCameraPreferences();
  return preferences?.preferredDeviceId || null;
};

/**
 * Validate if a saved camera preference is still valid
 * @param {Array} availableDevices - Array of available camera devices
 * @returns {Object} Object with isValid flag and deviceId if valid
 */
export const validateCameraPreference = (availableDevices) => {
  const preferences = loadCameraPreferences();

  if (!preferences?.preferredDeviceId) {
    return { isValid: false, deviceId: null };
  }

  const savedDeviceExists = availableDevices.some(
    (device) => device.deviceId === preferences.preferredDeviceId
  );

  if (!savedDeviceExists) {
    // Clear invalid preference
    clearCameraPreferences();
    return { isValid: false, deviceId: null };
  }

  return {
    isValid: true,
    deviceId: preferences.preferredDeviceId,
    deviceLabel: preferences.deviceLabel,
  };
};

/**
 * Get a user-friendly camera name
 * @param {Array} devices - Array of camera devices
 * @param {string} deviceId - The camera device ID
 * @returns {string} User-friendly camera name
 */
export const getCameraDisplayName = (devices, deviceId) => {
  const device = devices.find((d) => d.deviceId === deviceId);
  if (device?.label) {
    return device.label;
  }
  const index = devices.findIndex((d) => d.deviceId === deviceId);
  return `Camera ${index + 1}`;
};
